import { useMemo, useState } from 'react';
import type { FunctionComponent, SVGProps } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Select } from 'antd';
import Breadcrumbs from '../../components/Breadcrumbs';
import Pagination from '../../components/Pagination';
import {
  SearchIcon,
  NewEngagementIcon,
  CheckmarkIcon,
  KebabIcon,
  StatusPlanningIcon,
  TaskBloodIcon,
  TaskTeacherIcon,
  TaskMediaIcon,
  TaskRoadshowIcon,
} from '../../assets/icons';
import elena from '../../assets/avatars/elena-rodriguez.png';
import david from '../../assets/avatars/david-chen.png';
import admin from '../../assets/avatars/admin-user.png';
import sarah from '../../assets/avatars/sarah-jenkins.png';

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;
type TaskStatus = 'scheduled' | 'completed' | 'inprogress' | 'planning';
type Category = 'Health' | 'Cultural' | 'Media' | 'Campaign';

interface Task {
  id: string;
  name: string;
  location: string;
  category: Category;
  status: TaskStatus;
  date: string;
  coordinator: string;
  avatar: string;
  Tile: IconType;
  tileBg: string;
  tileColor: string;
}

const TASKS: Task[] = [
  { id: '1', name: 'Blood Donation Camp', location: 'City Square Mall, Main Plaza', category: 'Health', status: 'scheduled', date: 'July 15, 2024', coordinator: 'Dr. Anita Rao', avatar: elena, Tile: TaskBloodIcon, tileBg: 'bg-[#eff6ff]', tileColor: 'text-[#3b82f6]' },
  { id: '2', name: 'Teacher Felicitation', location: 'Central Auditorium', category: 'Cultural', status: 'completed', date: 'July 20, 2024', coordinator: 'Siddharth V.', avatar: david, Tile: TaskTeacherIcon, tileBg: 'bg-[#fff7ed]', tileColor: 'text-[#f97316]' },
  { id: '3', name: 'Media Meet', location: 'Press Club East', category: 'Media', status: 'inprogress', date: 'July 25, 2024', coordinator: 'Meera Desai', avatar: admin, Tile: TaskMediaIcon, tileBg: 'bg-[#eff6ff]', tileColor: 'text-[#3b82f6]' },
  { id: '4', name: 'Road Show', location: 'Zone 4 Neighborhoods', category: 'Campaign', status: 'planning', date: 'July 30, 2024', coordinator: 'K. P. Raghav', avatar: sarah, Tile: TaskRoadshowIcon, tileBg: 'bg-[#fff7ed]', tileColor: 'text-[#f97316]' },
];

const CATEGORY_PILL: Record<Category, string> = {
  Health: 'bg-[#dbeafe] text-[#1d4ed8]',
  Media: 'bg-[#dbeafe] text-[#1d4ed8]',
  Cultural: 'bg-[#e0e7ff] text-[#4338ca]',
  Campaign: 'bg-[#e0e7ff] text-[#4338ca]',
};

const GRID = 'grid grid-cols-[minmax(0,1fr)_120px_170px_140px_160px_80px] items-center';
const TH = 'text-xs font-bold uppercase tracking-[0.6px] text-[#6b7280]';

function StatusCell({ status }: { status: TaskStatus }) {
  if (status === 'scheduled')
    return (
      <span className="flex items-center gap-1.5 text-xs font-bold text-[#16a34a]">
        <span className="size-1.5 rounded-full bg-[#22c55e]" />
        SCHEDULED
      </span>
    );
  if (status === 'completed')
    return (
      <span className="flex items-center gap-1.5 text-xs font-bold text-[#4b5563]">
        <CheckmarkIcon className="size-4" />
        COMPLETED
      </span>
    );
  if (status === 'inprogress')
    return (
      <span className="flex items-center gap-1.5 text-xs font-bold text-[#2563eb]">
        <span className="h-1.5 w-3 rounded-sm bg-[#3b82f6]" />
        IN PROGRESS
      </span>
    );
  return (
    <span className="flex items-center gap-1.5 text-xs font-bold text-[#ea580c]">
      <StatusPlanningIcon className="size-4" />
      PLANNING
    </span>
  );
}

export default function EngagementDetailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const name = (location.state as { name?: string } | null)?.name ?? 'Guru Poornima';

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TASKS.filter((t) => {
      if (q && !(t.name.toLowerCase().includes(q) || t.location.toLowerCase().includes(q))) return false;
      if (status && t.status !== status) return false;
      if (category && t.category !== category) return false;
      return true;
    });
  }, [query, status, category]);

  return (
    <div className="flex flex-col gap-8">
      {/* Breadcrumb */}
      <Breadcrumbs items={[{ label: 'Engagements', to: '/user/engagements' }, { label: name }]} />

      {/* Title row */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-3">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-[#bbf7d0] bg-[#dcfce7] px-3 py-1 text-sm font-medium text-[#15803d]">
            <span className="size-1.5 rounded-full bg-[#22c55e]" />
            ACTIVE
          </span>
          <h1 className="text-2xl font-semibold leading-8 text-ink">{name}</h1>
        </div>
        <div className="rounded border border-line/20 bg-white px-4 py-3">
          <p className="text-base font-medium text-ink">Vikram Sethi</p>
          <p className="text-sm text-ink-secondary">Engagement Owner</p>
        </div>
      </div>

      {/* Overview */}
      <div className="flex flex-col gap-3">
        <span className="text-xs font-semibold uppercase tracking-[0.6px] text-[#64748b]">Overview</span>
        <div className="rounded-lg border border-line-soft bg-white px-5 py-5 shadow-card">
          <p className="text-sm leading-[22px] text-ink-secondary">
            A strategic community outreach initiative focused on honoring educators and strengthening
            grassroots connections through cultural reverence. This engagement aims to bridge the gap
            between local leadership and the educational community, fostering a spirit of mutual
            respect and long-term collaboration.
          </p>
        </div>
      </div>

      {/* Tasks card */}
      <div className="flex flex-col gap-5 rounded-xl border border-[#f3f4f6] bg-white p-4 shadow-card">
        {/* Toolbar */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-[384px]">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-[#9ca3af]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tasks.."
              className="w-full rounded-lg border border-[#e5e7eb] bg-white py-2.5 pl-10 pr-3 text-sm text-ink placeholder:text-[#9ca3af] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Select
              className="w-[150px]"
              value={status}
              onChange={setStatus}
              options={[
                { value: '', label: 'Status: All' },
                { value: 'scheduled', label: 'Scheduled' },
                { value: 'completed', label: 'Completed' },
                { value: 'inprogress', label: 'In Progress' },
                { value: 'planning', label: 'Planning' },
              ]}
            />
            <Select
              className="w-[160px]"
              value={category}
              onChange={setCategory}
              options={[
                { value: '', label: 'Category: All' },
                { value: 'Health', label: 'Health' },
                { value: 'Cultural', label: 'Cultural' },
                { value: 'Media', label: 'Media' },
                { value: 'Campaign', label: 'Campaign' },
              ]}
            />
            <button
              type="button"
              onClick={() => navigate(`/user/engagements/${id ?? ''}/tasks/new`, { state: { name } })}
              className="flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark"
            >
              <NewEngagementIcon className="size-4" />
              New Task
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-[#e5e7eb]">
          <div className="overflow-x-auto">
            <div className="min-w-[840px]">
              {/* Header */}
              <div className={[GRID, 'border-b border-[#e5e7eb] px-6 py-4'].join(' ')}>
                <span className={TH}>Task Details</span>
                <span className={TH}>Category</span>
                <span className={TH}>Status</span>
                <span className={TH}>Date</span>
                <span className={TH}>Coordinator</span>
                <span className={[TH, 'text-right'].join(' ')}>Actions</span>
              </div>

              {/* Rows */}
              {rows.length === 0 ? (
                <div className="px-6 py-12 text-center text-sm text-ink-secondary">No tasks found.</div>
              ) : (
                rows.map((t, i) => (
                  <div
                    key={t.id}
                    className={[GRID, 'px-6 py-4', i > 0 ? 'border-t border-[#f3f4f6]' : ''].join(' ')}
                  >
                    {/* Task details */}
                    <div className="flex items-center gap-4 pr-4">
                      <span className={['flex size-10 shrink-0 items-center justify-center rounded-full', t.tileBg, t.tileColor].join(' ')}>
                        <t.Tile className="size-5" />
                      </span>
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/user/engagements/${id ?? ''}/tasks/${t.id}`, {
                              state: { name, taskName: t.name },
                            })
                          }
                          className="truncate text-left text-sm font-semibold text-brand hover:underline"
                        >
                          {t.name}
                        </button>
                        <span className="truncate text-xs text-[#6b7280]">{t.location}</span>
                      </div>
                    </div>

                    {/* Category */}
                    <div>
                      <span className={['inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium', CATEGORY_PILL[t.category]].join(' ')}>
                        {t.category}
                      </span>
                    </div>

                    {/* Status */}
                    <div>
                      <StatusCell status={t.status} />
                    </div>

                    {/* Date */}
                    <span className="text-sm text-[#4b5563]">{t.date}</span>

                    {/* Coordinator */}
                    <div className="flex items-center gap-2.5">
                      <img src={t.avatar} alt={t.coordinator} className="size-8 shrink-0 rounded-full object-cover" />
                      <span className="text-sm font-medium text-[#374151]">{t.coordinator}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end">
                      <button type="button" aria-label="Task actions" className="text-ink-secondary transition-colors hover:text-brand">
                        <KebabIcon className="h-[15px] w-[3px] rotate-90" />
                      </button>
                    </div>
                  </div>
                ))
              )}

              {/* Footer */}
              <Pagination
                rangeStart={1}
                rangeEnd={rows.length}
                total={24}
                noun="activities"
                page={page}
                totalPages={Math.max(1, Math.ceil(24 / pageSize))}
                onChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={(v) => {
                  setPageSize(v);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
